<template>
    <div class="tw:max-w-xl tw:mx-auto">
        <!-- Header -->
        <h1 class="tw:text-2xl tw:font-bold tw:text-gray-900 tw:text-center tw:mb-6 tw:uppercase">
            {{ $t(esMembresia ? 'eventos.encontrado_membresia' : 'eventos.encontrado_abono', { simpatizante: textoSimpatizante }) }}!
        </h1>

        <ValidationObserver ref="formObserver" tag="form" @submit.prevent="handleConfirm">
            <!-- Summary Card -->
            <CardFront top-bar custom-classes="tw:mb-4 tw:overflow-hidden">
                <!-- Card Header -->
                <template #header>
                    <div>
                        <h2 class="tw:text-sm tw:font-bold tw:text-gray-600 tw:uppercase">
                            {{ $t(esMembresia ? 'eventos.datos_membresia' : 'eventos.datos_abono', { simpatizante: textoSimpatizante }) }}
                        </h2>
                    </div>
                </template>

                <div class="tw:flex tw:flex-col tw:gap-4">
                    <!-- Nombre Completo -->
                    <div class="tw:flex tw:flex-col tw:gap-1">
                        <span class="tw:text-xs tw:font-medium tw:text-gray-500 tw:uppercase">{{ $t('eventos.nombre_completo') }}</span>
                        <span class="tw:text-base tw:text-gray-900">{{ displayName }}</span>
                    </div>

                    <!-- Correo Electrónico -->
                    <div class="tw:flex tw:flex-col tw:gap-2">
                        <InputText
                            v-model="emailModel"
                            :label="$t('eventos.correo')"
                            name="email"
                            type="email"
                            :rules="'required|email'"
                            :disabled="loading"
                            icon='<path stroke-linecap="round" stroke-linejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75" />'
                        />
                        <p class="tw:text-xs tw:text-gray-500 tw:italic">
                            {{ $t('eventos.correo_puede_corregirse') }}
                        </p>
                    </div>

                    <!-- Contraseña -->
                    <div class="tw:flex tw:flex-col tw:gap-2">
                        <InputText
                            v-model="passwordModel"
                            :label="$t('eventos.contrasena')"
                            name="password"
                            type="password"
                            :rules="'required|min:8'"
                            :disabled="loading"
                            icon='<path stroke-linecap="round" stroke-linejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 1 0-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 0 0 2.25-2.25v-6.75a2.25 2.25 0 0 0-2.25-2.25H6.75a2.25 2.25 0 0 0-2.25 2.25v6.75a2.25 2.25 0 0 0 2.25 2.25Z" />'
                        />
                        <p class="tw:text-xs tw:text-gray-500 tw:italic">
                            {{ $t('eventos.contrasena_minimo') }}
                        </p>
                    </div>
                </div>
            </CardFront>

            <!-- Info Notice -->
            <div class="tw:bg-gray-100 tw:rounded-lg tw:p-4 tw:flex tw:items-start tw:gap-3 tw:mb-6 tw:mt-4">
                <svg class="tw:w-5 tw:h-5 tw:text-gray-500 tw:flex-shrink-0 tw:mt-0.5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" d="m11.25 11.25.041-.02a.75.75 0 0 1 1.063.852l-.708 2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9-3.75h.008v.008H12v-.008Z" />
                </svg>
                <p class="tw:text-sm tw:text-gray-600 tw:mb-0">
                    {{ $t(esMembresia ? 'eventos.aviso_vinculacion_membresia' : 'eventos.aviso_vinculacion_abono', { simpatizante: textoSimpatizante }) }}
                </p>
            </div>

            <!-- Error -->
            <div v-if="errorKey" class="tw:mb-4 tw:bg-red-50 tw:border tw:border-red-200 tw:text-red-800 tw:px-4 tw:py-3 tw:rounded-lg">
                <p class="tw:font-bold tw:text-sm">{{ $t('error') }}</p>
                <p class="tw:text-sm tw:mt-1">{{ $t(errorKey) }}</p>
            </div>

            <!-- Actions -->
            <div class="tw:flex tw:flex-col sm:flex-row tw:gap-4 tw:items-center tw:justify-between">
                <Button variant="dark" custom-class="tw:w-full sm:tw:w-auto" @click="$emit('volver')" data-cy="btn-volver">
                    {{ $t('carrito.volver') }}
                </Button>
                <Button type="submit" variant="success" :loading="loading" custom-class="tw:w-full sm:tw:w-auto" data-cy="btn-confirmar">
                    <span v-if="loading">...</span>
                    <span v-else>{{ $t('eventos.confirmar_vinculacion') }}</span>
                </Button>
            </div>
        </ValidationObserver>
    </div>
</template>

<script setup>
    import { ref, computed, watch } from 'vue';
    import { ValidationObserver } from 'vee-validate';
    import CardFront from '@/modules/front/ui/CardFront.vue';
    import InputText from '@/modules/front/ui/InputText.vue';
    import Button from '@/modules/front/ui/Button.vue';
    import store from '@/store';

    const teatro = computed(() => store.getters.getTeatro || {});
    const textoSimpatizante = computed(() => teatro.value.texto_simpatizante || '');

    const props = defineProps({
        displayName: {
            type: String,
            default: ''
        },
        emailToBind: {
            type: String,
            default: ''
        },
        entitlementType: {
            type: String,
            default: 'abono'
        },
        errorKey: {
            type: String,
            default: ''
        },
        loading: {
            type: Boolean,
            default: false
        }
    });
    const esMembresia = computed(() => props.entitlementType === 'membresia');

    const emit = defineEmits(['confirm', 'volver']);

    const formObserver = ref(null);

    // Local state for email, synced with prop
    const emailLocal = ref(props.emailToBind);

    watch(() => props.emailToBind, newVal => {
        emailLocal.value = newVal;
    });

    // Computed v-model for two-way binding between InputText and local state
    const emailModel = computed({
        get: () => emailLocal.value,
        set: value => { emailLocal.value = value; }
    });

    // Local state for password
    const passwordLocal = ref('');

    const passwordModel = computed({
        get: () => passwordLocal.value,
        set: value => { passwordLocal.value = value; }
    });

    const handleConfirm = () => {
        emit('confirm', { email: emailLocal.value, password: passwordLocal.value });
    };
</script>
